import type { TotemTableSortState } from '@totem/vue';

import { getEspaceDistributeurServicesApi } from '@/core/lib/ApiUtils';
import Cache, { assembleCacheKey } from '@/core/lib/Cache';
import { formatNom, formatNomComplet, formatPrenom } from '@/core/lib/NomPrenomUtils';
import { DEFAULT_PAGE_SIZE, type PaginationApiResponse } from '@/core/lib/PaginationUtils';
import { getLoggedInUser } from '@/domains/authentification/lib/Authentication';
import { captureError } from '@/domains/logger/lib/ApmUtils';
import { type ContexteRsiItem, type ContexteRsiItemApi, mapContexteRsiItemApiToContexteRsiItem, mapContexteRsiItemToContexteRsiItemApi } from '@/domains/utilisateurs/lib/ContexteRsiApi';
import { type CiviliteCode, type RepetitionIndex, RoleMap } from '@/domains/utilisateurs/lib/UtilisateurUtils';

export class UtilisateurApiError extends Error {}

const COMPTE_CACHE_KEY = 'compte';
const ADMIN_COUNT_CACHE_KEY = 'admin-count';
const UTILISATEUR_CACHE_KEY_FORMAT = 'utilisateur';

function getUtilisateurCacheKey(id: string) {
  return assembleCacheKey(UTILISATEUR_CACHE_KEY_FORMAT, id);
}

// Api types ----------------------------------------------------------------------------------------------------------

interface AddressApi {
  numero: number | null;
  indice_repetition: string | null;
  voie: string | null;
  complement: string | null;
  code_postal: string | null;
  ville: string | null;
}

interface NomPrenomApi {
  nom: string;
  prenom: string;
}

interface CiviliteApi {
  civilite: CiviliteCode;
}

interface EmailApi {
  email: string;
}

interface TelephoneApi {
  telephone: string | null;
}

interface LoginApi {
  login: string | null;
}

interface RoleApi {
  role: string;
}

interface IntermediaireApi extends CiviliteApi, NomPrenomApi, EmailApi, TelephoneApi {
  id: string | null;
  adresse_postale: AddressApi | null;
}

interface DistributeurApi extends RoleApi {
  id: string;
  intermediaire: IntermediaireApi;
  contexte_rsi: ContexteRsiItemApi[];
}

interface AccesApi {
  acces: {
    distributeurs: DistributeurApi[];
  };
}

export interface UtilisateurApi extends NomPrenomApi, EmailApi, LoginApi, AccesApi {
  id: string;
  date_creation: string;
  date_modification: string;
  conditions_generales_utilisation?: ConditionsGeneralesUtilisationApi;
}

export interface UtilisateurPayloadApi extends NomPrenomApi, EmailApi, AccesApi {}

interface InfosIntermediaire extends CiviliteApi, NomPrenomApi, TelephoneApi {
  adresse_postale: AddressApi | null;
}

export interface AssocierUtilisateurPayloadApi extends EmailApi, RoleApi {
  distributeur_id: string;
  contexte_rsi_ids: string[];
  infos_intermediaire: InfosIntermediaire;
}

export type SearchResultApi = PaginationApiResponse<UtilisateurApi>;

interface ExisteApi {
  existe: boolean;
}

export interface ConditionsGeneralesUtilisationApi {
  is_derniere_version_acceptee: boolean;
}

// App types ----------------------------------------------------------------------------------------------------------

export interface Filters {
  contexteRsiList: string[];
  roleList: string[];
}

export interface Address {
  number: string | undefined;
  repetitionIndex: RepetitionIndex | undefined;
  street: string | undefined;
  complement: string | undefined;
  postalCode: string | undefined;
  city: string | undefined;
}

interface FirstnameLastname {
  firstname: string;
  lastname: string;
}

interface EmailPhoneAddress {
  email: string;
  phone: string | undefined;
  address: Address | undefined;
}

interface CodeRole {
  codeRole: string;
}

interface Login {
  login: string | undefined;
}

interface IntermediaireId {
  intermediaireId: string | undefined;
}

interface ContexteRsi {
  contexteRsi: ContexteRsiItem[];
}

export interface Utilisateur
  extends FirstnameLastname, EmailPhoneAddress, CodeRole, Login, IntermediaireId, ContexteRsi {
  id: string;
  title: CiviliteCode;
  fullname?: string;
  createdAt: Date;
  updatedAt: Date;
  conditionsGeneralesUtilisation?: ConditionsGeneralesUtilisation;
}

type UtilisateurId = Utilisateur['id'];

export interface UtilisateurPayload
  extends FirstnameLastname, EmailPhoneAddress, CodeRole, IntermediaireId, ContexteRsi {
  id?: string;
  title: CiviliteCode;
}

export interface SearchResultItem extends FirstnameLastname, CodeRole, Login, ContexteRsi {
  id: string;
  fullname?: string;
}

export interface SearchResult {
  total: number;
  page: number;
  utilisateurs: SearchResultItem[];
}

export const defaultSortState: Readonly<Required<TotemTableSortState>> = {
  key: 'nom_prenom',
  isDesc: false,
} as const;

export interface ConditionsGeneralesUtilisation {
  isDerniereVersionAcceptee: boolean;
}

// Mappers ----------------------------------------------------------------------------------------------------------

function mapAddressToAddressApi({
  number,
  repetitionIndex,
  street,
  complement,
  postalCode,
  city,
}: Address): AddressApi {
  return {
    numero: number === undefined ? null : Number(number),
    indice_repetition: repetitionIndex ?? null,
    voie: street ?? null,
    complement: complement ?? null,
    code_postal: postalCode ?? null,
    ville: city ?? null,
  };
}

function mapAddressApiToAddress({
  numero,
  indice_repetition,
  voie,
  complement,
  code_postal,
  ville,
}: AddressApi): Address {
  return {
    number: numero === null ? undefined : numero.toString(),
    repetitionIndex: indice_repetition ? (indice_repetition as RepetitionIndex) : undefined,
    street: voie ?? undefined,
    complement: complement ?? undefined,
    postalCode: code_postal ?? undefined,
    city: ville ?? undefined,
  };
}

function findDistributeurApiOrFail(id: string, distributeursApi: DistributeurApi[]): DistributeurApi {
  const distributeur = distributeursApi.find(distributeurApi => distributeurApi.id === id);

  if (distributeur === undefined) {
    throw new Error('Distributeur introuvable.');
  }

  return distributeur;
}

async function mapUtilisateurApiToUtilisateur(utilisateur: UtilisateurApi): Promise<Utilisateur> {
  const user = await getLoggedInUser();
  const distributeur = findDistributeurApiOrFail(user.distributeur.id, utilisateur.acces.distributeurs);
  const { intermediaire, contexte_rsi, role } = distributeur;

  return {
    id: utilisateur.id,
    intermediaireId: intermediaire.id ?? undefined,
    codeRole: role,
    login: utilisateur.login ?? undefined,
    createdAt: new Date(utilisateur.date_creation),
    updatedAt: new Date(utilisateur.date_modification),
    firstname: formatPrenom(utilisateur.prenom),
    lastname: formatNom(utilisateur.nom),
    fullname: formatNomComplet(utilisateur.prenom, utilisateur.nom),
    title: intermediaire.civilite,
    email: utilisateur.email,
    phone: intermediaire.telephone ?? undefined,
    address: intermediaire.adresse_postale ? mapAddressApiToAddress(intermediaire.adresse_postale) : undefined,
    contexteRsi: contexte_rsi.map(mapContexteRsiItemApiToContexteRsiItem),
    conditionsGeneralesUtilisation: utilisateur.conditions_generales_utilisation
      ? mapConditionsGeneralesUtilisationApiToConditionsGeneralesUtilisation(
          utilisateur.conditions_generales_utilisation,
        )
      : undefined,
  };
}

async function mapSearchResultApiToSearchResult({ content, page, total }: SearchResultApi): Promise<SearchResult> {
  const user = await getLoggedInUser();

  return {
    total,
    page,
    utilisateurs: content?.map(utilisateur => {
      const distributeur = findDistributeurApiOrFail(user.distributeur.id, utilisateur.acces.distributeurs);
      const { contexte_rsi, role } = distributeur;

      return {
        id: utilisateur.id,
        firstname: formatPrenom(utilisateur.prenom),
        lastname: formatNom(utilisateur.nom),
        fullname: formatNomComplet(utilisateur.prenom, utilisateur.nom),
        codeRole: role,
        login: utilisateur.login ?? undefined,
        contexteRsi: contexte_rsi.map(mapContexteRsiItemApiToContexteRsiItem),
      };
    }) ?? [],
  };
}

async function mapUtilisateurPayloadToUtilisateurPayloadApi({
  firstname,
  lastname,
  email,
  title,
  phone,
  codeRole,
  contexteRsi,
  address,
  intermediaireId,
}: UtilisateurPayload): Promise<UtilisateurPayloadApi> {
  const user = await getLoggedInUser();
  const prenom = formatPrenom(firstname);
  const nom = formatNom(lastname);

  return {
    prenom,
    nom,
    email,
    acces: {
      distributeurs: [
        {
          id: user.distributeur.id,
          role: codeRole,
          intermediaire: {
            id: intermediaireId ?? null,
            prenom,
            nom,
            civilite: title,
            telephone: phone ?? null,
            email,
            adresse_postale: address ? mapAddressToAddressApi(address) : null,
          },
          contexte_rsi: contexteRsi.map(mapContexteRsiItemToContexteRsiItemApi),
        },
      ],
    },
  };
}

async function mapUtilisateurPayloadToAssocierUtilisateurPayloadApi({
  firstname,
  lastname,
  email,
  title,
  phone,
  codeRole,
  contexteRsi,
  address,
}: UtilisateurPayload): Promise<AssocierUtilisateurPayloadApi> {
  const user = await getLoggedInUser();
  const prenom = formatPrenom(firstname);
  const nom = formatNom(lastname);

  return {
    distributeur_id: user.distributeur.id,
    email,
    role: codeRole,
    contexte_rsi_ids: contexteRsi.map(({ code }) => code),
    infos_intermediaire: {
      civilite: title,
      nom,
      prenom,
      telephone: phone ?? null,
      adresse_postale: address ? mapAddressToAddressApi(address) : null,
    },
  };
}

export function mapConditionsGeneralesUtilisationApiToConditionsGeneralesUtilisation({
  is_derniere_version_acceptee,
}: ConditionsGeneralesUtilisationApi): ConditionsGeneralesUtilisation {
  return {
    isDerniereVersionAcceptee: is_derniere_version_acceptee,
  };
}

// Calls ----------------------------------------------------------------------------------------------------------

export async function isEmailAvailable(email: string, contexteDistributeur = true): Promise<boolean> {
  return getEspaceDistributeurServicesApi()
    .then(api =>
      api.post<ExisteApi>('/utilisateurs/command/existe', {
        email,
        contexte_distributeur: contexteDistributeur,
      }),
    )
    .then(({ data }) => !data.existe);
}

const cacheAdminCount = new Cache<Promise<number>>();
const cacheUtilisateur = new Cache<Promise<Utilisateur>>();

export async function saveUtilisateur(utilisateur: UtilisateurPayload): Promise<void> {
  cacheAdminCount.clear(ADMIN_COUNT_CACHE_KEY);

  if (utilisateur.id) {
    cacheUtilisateur.clear(getUtilisateurCacheKey(utilisateur.id));
  }

  await Promise.all([
    getEspaceDistributeurServicesApi(),
    mapUtilisateurPayloadToUtilisateurPayloadApi(utilisateur),
  ]).then(([api, payload]) => {
    let url = '/utilisateurs';

    if (utilisateur.id !== undefined) {
      url += `/${encodeURIComponent(utilisateur.id)}`;
    }

    return api.request({
      method: utilisateur.id === undefined ? 'POST' : 'PUT',
      url,
      data: payload,
    });
  });
}

export async function associerUtilisateur(utilisateur: UtilisateurPayload): Promise<void> {
  await Promise.all([
    getEspaceDistributeurServicesApi(),
    mapUtilisateurPayloadToAssocierUtilisateurPayloadApi(utilisateur),
  ]).then(([api, payload]) =>
    api.post<AssocierUtilisateurPayloadApi>('/utilisateurs/associer-distributeur-supplementaire', payload),
  );
}

export async function getUtilisateur(id: string, force = false): Promise<Utilisateur> {
  const cacheKey = getUtilisateurCacheKey(id);

  if (force) {
    cacheUtilisateur.clear(cacheKey);
  }

  return cacheUtilisateur.getOrSet(cacheKey, () =>
    getEspaceDistributeurServicesApi()
      .then(api => api.get<UtilisateurApi>(`/utilisateurs/${id}`))
      .then(({ data }) => mapUtilisateurApiToUtilisateur(data)),
  );
}

export function getCompteOrFail(force = false): Promise<Utilisateur> {
  if (force) {
    cacheUtilisateur.clear(COMPTE_CACHE_KEY);
  }

  return cacheUtilisateur.getOrSet(COMPTE_CACHE_KEY, () =>
    getEspaceDistributeurServicesApi()
      .then(api => api.get<UtilisateurApi>('/compte'))
      .then(({ data }) => mapUtilisateurApiToUtilisateur(data)),
  );
}

export async function getCompte(force = false): Promise<Utilisateur | undefined> {
  try {
    return await getCompteOrFail(force);
  } catch (e) {
    // Unlike getCompteOrFail, this one is silent if something goes wrong
    if (e) {
      console.error(e);
      captureError(e);
    }

    return undefined;
  }
}

export async function updateCompte(utilisateur: UtilisateurPayload): Promise<void> {
  cacheUtilisateur.clear(COMPTE_CACHE_KEY);

  await Promise.all([
    getEspaceDistributeurServicesApi(),
    mapUtilisateurPayloadToUtilisateurPayloadApi(utilisateur),
  ]).then(([api, payload]) => api.put<AssocierUtilisateurPayloadApi>('/compte', payload));
}

export async function accepterConditionsGeneralesUtilisation(): Promise<void> {
  cacheUtilisateur.clear(COMPTE_CACHE_KEY);

  await getEspaceDistributeurServicesApi().then(api => api.post('/compte/accepter-cgu'));
}

export async function deleteUtilisateur(
  id: UtilisateurId,
  utilisateurIdToTransferProjectsTo?: UtilisateurId,
): Promise<void> {
  if (id === utilisateurIdToTransferProjectsTo) {
    throw new UtilisateurApiError('Unable to transfer projects to the user being deleted.');
  }

  cacheAdminCount.clear(ADMIN_COUNT_CACHE_KEY);
  cacheUtilisateur.clear(getUtilisateurCacheKey(id));

  await getEspaceDistributeurServicesApi().then(api =>
    api.delete(`/utilisateurs/${encodeURIComponent(id)}`, {
      params: utilisateurIdToTransferProjectsTo
        ? { uuid_pour_transfert: utilisateurIdToTransferProjectsTo }
        : undefined,
    }),
  );
}

export async function getUtilisateursSearchResult(
  page = 0,
  sortState?: TotemTableSortState,
  keyword?: string,
  filters?: Filters,
): Promise<SearchResult> {
  const queryParams = new URLSearchParams([
    ['page', page.toString()],
    ['page_size', DEFAULT_PAGE_SIZE.toString()],
  ]);

  if (sortState?.key) {
    queryParams.set('sort', sortState.key);
    queryParams.set('sort_direction', sortState.isDesc ? 'DESC' : 'ASC');
  } else {
    queryParams.set('sort', defaultSortState.key);
    queryParams.set('sort_direction', defaultSortState.isDesc ? 'DESC' : 'ASC');
  }

  if (keyword) {
    queryParams.append('recherche', keyword.trim());
  }

  if (filters) {
    const { contexteRsiList, roleList } = filters;

    for (const code of contexteRsiList) {
      queryParams.append('contexte_rsi', code);
    }

    for (const code of roleList) {
      queryParams.append('roles', code);
    }
  }

  return getEspaceDistributeurServicesApi()
    .then(api => api.get<SearchResultApi>(`/utilisateurs?${queryParams.toString()}`))
    .then(({ data }) => mapSearchResultApiToSearchResult(data));
}

export function getAdminCount(): Promise<number> {
  return cacheAdminCount.getOrSet(ADMIN_COUNT_CACHE_KEY, () => {
    const queryParams = new URLSearchParams([
      ['page', '0'],
      ['page_size', '1'],
      ['roles', RoleMap.ADMIN],
    ]);

    return getEspaceDistributeurServicesApi()
      .then(api => api.get<SearchResultApi>(`/utilisateurs?${queryParams.toString()}`))
      .then(({ data }) => data.total);
  });
}

interface CountProjectsApi {
  total: number;
}

export async function countProjects(utilisateurId: Utilisateur['id']): Promise<number> {
  return getEspaceDistributeurServicesApi()
    .then(api => api.get<CountProjectsApi>(`/utilisateurs/${encodeURIComponent(utilisateurId)}/projets/count`))
    .then(({ data: { total } }) => total);
}

type TransferTargetUtilisateurApi = PaginationApiResponse<UtilisateurApi>;

export type TransferTargetUtilisateur = Pick<Utilisateur, 'id' | 'firstname' | 'lastname' | 'fullname'>;

export interface TransferTargetUtilisateurSuggestion extends TransferTargetUtilisateur {
  value: string;
  label?: string;
}

function mapUtilisateurApiToTransferTargetUtilisateur({
  id,
  prenom: firstname,
  nom: lastname,
}: UtilisateurApi): TransferTargetUtilisateur {
  return {
    id,
    firstname,
    lastname,
    fullname: formatNomComplet(firstname, lastname, true),
  };
}

export async function getTransferTargetUtilisateurs(
  utilisateurIdToDelete: string,
  contexteRsiCodeList: ContexteRsiItem['code'][],
): Promise<TransferTargetUtilisateur[]> {
  const params = new URLSearchParams({
    page: '0',
    page_size: '10000',
  });

  for (const contexteRsiCode of contexteRsiCodeList) {
    params.append('contexte_rsi', contexteRsiCode);
  }

  return getEspaceDistributeurServicesApi()
    .then(api => api.get<TransferTargetUtilisateurApi>(`/utilisateurs?${params.toString()}`))
    .then(({ data }) => {
      const content = data.content;

      if (!Array.isArray(content)) {
        return [];
      }

      return content.reduce<TransferTargetUtilisateur[]>((acc, utilisateur) => {
        // Remove the utilisateur being deleted from results
        if (utilisateur.id !== utilisateurIdToDelete) {
          acc.push(mapUtilisateurApiToTransferTargetUtilisateur(utilisateur));
        }

        return acc;
      }, []);
    });
}

export interface CheckTransferUtilisateurApi {
  non_transferables: { code: string; libelle: string }[];
}

export type CheckTransferUtilisateur = { code: string; label: string }[];

function mapCheckTransferUtilisateurApiToCheckTransferUtilisateur({
  non_transferables: nonTransferableList,
}: CheckTransferUtilisateurApi) {
  return nonTransferableList.map(({ code, libelle: label }) => ({
    code,
    label,
  }));
}

export function checkTransferUtilisateur(
  fromUtilisateurId: UtilisateurId,
  toUtilisateurId: UtilisateurId,
): Promise<CheckTransferUtilisateur> {
  return getEspaceDistributeurServicesApi().then(api =>
    api
      .post<CheckTransferUtilisateurApi>('/projets/command/verifier-transfert', {
        id_ancien_utilisateur: fromUtilisateurId,
        id_nouvel_utilisateur: toUtilisateurId,
      })
      .then(({ data }) => mapCheckTransferUtilisateurApiToCheckTransferUtilisateur(data)),
  );
}
